import { useEffect, useRef, useState } from "react";

/**
 * Phase 6ak (Hotfix Z.4) — Engine-DOWN false-positive debouncer.
 *
 * The engine-alive probe is a SIP OPTIONS round-trip over UDP. UDP can
 * lose a single datagram for many benign reasons (kernel buffer
 * overflow on a busy host, brief GC pause in the engine, conntrack
 * drop on the NIC under load). One missed reply made the badge flip
 * to "DOWN" instantly, even though the very next poll one second
 * later usually came back fine. Operators reported persistent
 * flicker.
 *
 * This hook smooths that out:
 *   * `rawAlive === true`  → "up" immediately (positive signals are
 *     trusted — a SIP/2.0 reply cannot be fabricated by packet loss).
 *   * `rawAlive === false` → counts CONSECUTIVE falses. After
 *     `falsesNeeded` (default 3), flip to "down". Before that, show
 *     "checking" (transitional/warn state). Any `true` resets.
 *   * `rawAlive == null`   → "checking" (initial render / no data).
 *
 * Returns "up" | "down" | "checking".
 *
 * Implementation note (important): we need to count EVERY poll where
 * `rawAlive === false`, even when the previous value was also `false`
 * (a stuck-DOWN engine never changes the primitive, so React's effect
 * dependency-tracking would skip the effect). We solve this by also
 * watching a `tick` value the caller can supply — typically `stats`
 * or a monotonic counter that bumps on every poll. When `tick` isn't
 * supplied we fall back to incrementing a ref on every render, which
 * still works because the parent re-renders on each poll.
 */
export function useEngineAliveDebounced(rawAlive, falsesNeeded = 3, tick) {
  const [state, setState] = useState("checking");
  const falseCountRef = useRef(0);
  // Capture the latest rawAlive in a ref so the effect always sees the
  // freshest value even when React batches consecutive identical
  // primitives.
  const aliveRef = useRef(rawAlive);
  aliveRef.current = rawAlive;

  useEffect(() => {
    const v = aliveRef.current;
    if (v === true) {
      falseCountRef.current = 0;
      setState((prev) => (prev === "up" ? prev : "up"));
      return;
    }
    if (v === false) {
      falseCountRef.current += 1;
      if (falseCountRef.current >= falsesNeeded) {
        setState((prev) => (prev === "down" ? prev : "down"));
      } else {
        setState((prev) => (prev === "checking" ? prev : "checking"));
      }
      return;
    }
    // null / undefined → no data yet.
    falseCountRef.current = 0;
    setState((prev) => (prev === "checking" ? prev : "checking"));
    // We intentionally re-run on every change of either `rawAlive`
    // (covers the transition case) OR `tick` (covers the stuck-false
    // case where rawAlive primitive doesn't change between polls).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawAlive, tick, falsesNeeded]);

  return state;
}
